import React, { useEffect, useState } from 'react';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import InvoiceListPageActions from './Invoices/components/InvoiceListPageActions';
import CreditNoteListPageActions from './creditNotes/components/CreditNoteListPageActions';
import InvoiceListPage from './Invoices/pages/InvoiceListPage';
import CreditNoteListPage from './creditNotes/pages/CreditNoteListPage';
import { useSelector } from 'react-redux';
import TopBar from '../components/TopBar';
import BulkUploadButton from './bulkUpload/components/BulkUploadButton';
import { useTranslatedMessages } from '../hooks/useTranslatedMessages';
import { usePermissions } from '../hooks/usePermissions';
import {
  CREATE_PURCHASE_CREDIT_NOTE,
  CREATE_PURCHASE_INVOICE,
  CREATE_SALE_CREDIT_NOTE,
  CREATE_SALE_INVOICE,
  READ_PURCHASE_CREDIT_NOTE,
  READ_PURCHASE_INVOICE,
  READ_SALE_CREDIT_NOTE,
  READ_SALE_INVOICE,
} from '../constants/eInvoicePermissions';
import AccessDeniedCard from '../components/AccessDeniedCard';
import { useRoleModuleLabel } from '../hooks/useRoleModuleLabel';
import { PURCHASE_MODULE_KEY, SALES_MODULE_KEY } from './settings/constants/roleModules';

const DocumentListPage = ({ isPurchase }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const { eInvoiceForm } = useTranslatedMessages();
  const { document_list } = eInvoiceForm;
  const { loading, hasEInvoicePermission } = usePermissions();
  const accessDeniedModuleTitle = useRoleModuleLabel(isPurchase ? PURCHASE_MODULE_KEY : SALES_MODULE_KEY);
  const tabs = [
    { key: 'invoices', label: document_list.tab_invoices },
    { key: 'credit-notes', label: document_list.tab_credit_notes },
  ];
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'invoices';
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const canReadInvoices = isPurchase
    ? hasEInvoicePermission(READ_PURCHASE_INVOICE)
    : hasEInvoicePermission(READ_SALE_INVOICE);
  const canReadCreditNotes = isPurchase
    ? hasEInvoicePermission(READ_PURCHASE_CREDIT_NOTE)
    : hasEInvoicePermission(READ_SALE_CREDIT_NOTE);
  const invoiceBulkUploadPermission = isPurchase ? CREATE_PURCHASE_INVOICE : CREATE_SALE_INVOICE;
  const creditNoteBulkUploadPermission = isPurchase ? CREATE_PURCHASE_CREDIT_NOTE : CREATE_SALE_CREDIT_NOTE;
  const documentsBasePath = isPurchase ? '/purchases/documents' : '/sales/documents';

  const canReadTab = (tab) => {
    if (tab === 'invoices') {
      return canReadInvoices;
    }
    if (tab === 'credit-notes') {
      return canReadCreditNotes;
    }
    return true;
  };

  useEffect(() => {
    setSelectedTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!canReadTab(initialTab)) {
      if (initialTab === 'invoices' && canReadCreditNotes) {
        navigate(`${documentsBasePath}?tab=credit-notes`, { replace: true });
      } else if (initialTab === 'credit-notes' && canReadInvoices) {
        navigate(`${documentsBasePath}?tab=invoices`, { replace: true });
      }
    }
  }, [loading, initialTab, canReadInvoices, canReadCreditNotes, navigate, documentsBasePath]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    navigate(isPurchase ? `/purchases/documents?tab=${newValue}` : `/sales/documents?tab=${newValue}`);
  };

  const getTopBarTitle = (tab) => {
    switch (tab) {
      case 'invoices':
        return document_list.title_invoices;
      case 'credit-notes':
        return document_list.title_credit_notes;
      default:
        return document_list.title_default;
    }
  };

  const getTopBarActions = (tab, user, isPurchase) => {
    const redirectAfterSubmit = isPurchase ? '/purchases/bulk-upload' : '/sales/bulk-upload';
    const subCategory = isPurchase ? 'PURCHASES' : 'SALES';

    switch (tab) {
      case 'invoices':
        return (
          <Stack direction="row" spacing={1}>
            <BulkUploadButton
              category="E_INVOICE"
              subCategory={subCategory}
              redirectAfterSubmit={redirectAfterSubmit}
              requiredPermission={invoiceBulkUploadPermission}
            />
            <InvoiceListPageActions isPurchase={isPurchase} />
          </Stack>
        );
      case 'credit-notes':
        return (
          <Stack direction="row" spacing={1}>
            <BulkUploadButton
              category="E_INVOICE"
              subCategory={subCategory}
              redirectAfterSubmit={redirectAfterSubmit}
              requiredPermission={creditNoteBulkUploadPermission}
            />
            <CreditNoteListPageActions
              isPurchase={isPurchase}
              actions={[
                {
                  label: document_list.create_credit_note,
                  action: 'create',
                  target: isPurchase
                    ? '/purchases/documents/credit-notes/create'
                    : '/sales/documents/credit-notes/create',
                },
              ]}
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  const getListPage = (tab, isPurchase) => {
    if (!canReadTab(tab)) {
      return <AccessDeniedCard title={accessDeniedModuleTitle} />;
    }

    switch (tab) {
      case 'invoices':
        return <InvoiceListPage isPurchase={isPurchase} />;
      case 'credit-notes':
        return <CreditNoteListPage isPurchase={isPurchase} />;
      default:
        return null;
    }
  };

  return (
    <Stack direction="column" spacing={3}>
      <TopBar title={getTopBarTitle(selectedTab)} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Tabs value={selectedTab} onChange={handleTabChange}>
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
        {getTopBarActions(selectedTab, user, isPurchase)}
      </Box>
      <Box>{getListPage(selectedTab, isPurchase)}</Box>
    </Stack>
  );
};

export default DocumentListPage;
